import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useActiveSteps } from 'hooks/useActiveSteps'
import { useExperimentalFeatures } from 'hooks/useExperimentalFeatures'
import { usePostPaymentFlowConfig } from 'hooks/usePostPaymentFlowConfig'

import { getPageIdFromPathName } from 'helpers/getPageIdFromPathName'

import { EMAIL_PAGE_IDS, INTRO_LOADER_PAGE_IDS, PageId } from 'page-constants'

export const useGetPageInfo = () => {
  const { pathname } = useLocation()
  const steps = useActiveSteps()
  const { pages: postPaymentPages } = usePostPaymentFlowConfig()
  const { isAyah47PreloadScreenTest } = useExperimentalFeatures()

  const currentPageId = getPageIdFromPathName(pathname)

  const currentPageIndex = steps.findIndex(({ id }) => id === currentPageId)

  const isFirstPage = currentPageIndex === 0

  const isSecondPage = currentPageIndex === 1

  const firstQuestionPageIndex = steps.findIndex(
    ({ id }) => !INTRO_LOADER_PAGE_IDS.includes(id),
  )

  const isFirstQuestionPage =
    currentPageIndex !== -1 && currentPageIndex === firstQuestionPageIndex

  const isPostPaymentPage = postPaymentPages.includes(currentPageId as PageId)

  const isPreloadAnimatedBreak =
    currentPageId === PageId.ANIMATED_BREAK && isAyah47PreloadScreenTest

  const pagesWithProgressBar = useMemo(
    () => steps.filter(({ hasProgressBar }) => hasProgressBar),
    [steps],
  )

  const { hasHeader, hasProgressbar, isSkippableStep } = useMemo(
    () => ({
      hasHeader:
        !isPreloadAnimatedBreak &&
        (steps[currentPageIndex]?.hasHeader || isPostPaymentPage),
      hasProgressbar: steps[currentPageIndex]?.hasProgressBar,
      isSkippableStep: steps[currentPageIndex]?.isSkippable,
    }),
    [currentPageIndex, isPostPaymentPage, isPreloadAnimatedBreak, steps],
  )

  const isSubscriptionsPage = !!steps[currentPageIndex]?.isSubscriptions

  let currentSubscriptionPageId = '' as PageId
  let currentPaymentPageId = '' as PageId
  let currentUpsellPageId = '' as PageId

  steps.forEach(({ isSubscriptions, isPayment, isUpsell, id }) => {
    if (isSubscriptions && !currentSubscriptionPageId) {
      currentSubscriptionPageId = id
    }

    if (isPayment && !currentPaymentPageId) currentPaymentPageId = id
    if (isUpsell && !currentUpsellPageId) currentUpsellPageId = id
  })

  const currentCancelOfferSubscriptionsPageId = steps.find((page) =>
    page.id.includes('cancelOfferSubscriptionV'),
  )?.id

  const currentCancelOfferQuestionPageId = steps.find((page) =>
    page.id.includes('cancelOfferQuestionV'),
  )?.id

  const currentEmailPageId = steps.find((page) =>
    EMAIL_PAGE_IDS.includes(page.id),
  )?.id

  const upsellPagesIds = useMemo(
    () => steps.filter((step) => step.isUpsell).map((page) => page.id),
    [steps],
  )

  const isPaymentPage = currentPaymentPageId === currentPageId

  return {
    currentPageId,
    hasHeader,
    hasProgressbar,
    pagesWithProgressBar,
    currentSubscriptionPageId,
    currentPaymentPageId,
    currentUpsellPageId,
    isFirstPage,
    isFirstQuestionPage,
    isSecondPage,
    isSkippableStep,
    isPaymentPage,
    isPostPaymentPage,
    isSubscriptionsPage,
    currentCancelOfferSubscriptionsPageId,
    currentCancelOfferQuestionPageId,
    currentEmailPageId,
    upsellPagesIds,
  }
}
